import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import { useClubTheme } from '../contexts/ClubThemeContext';

// MUI
import {
  Box,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Link,
  Avatar,
  useMediaQuery,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  Button
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';

// Pour lien interne (fiche joueur)
import { Link as RouterLink } from 'react-router-dom';

/* ================================================
   Design Tokens — palette & spacing centralisés
   ================================================ */
const DESIGN = {
  // Surfaces
  bgPage: '#f5f7fa',
  bgCard: '#ffffff',
  bgCardHeader: '#f8fafc',
  bgTabBar: '#ffffff',
  // Accent
  primary: '#4f46e5',
  primaryLight: '#eef2ff',
  primaryDark: '#3730a3',
  // Rank medals
  gold: '#f59e0b',
  silver: '#94a3b8',
  bronze: '#d97706',
  // Results
  win: '#10b981',
  loss: '#ef4444',
  // Text
  textPrimary: '#1e293b',
  textSecondary: '#64748b',
  textMuted: '#94a3b8',
  // Borders
  border: '#e2e8f0',
  borderLight: '#f1f5f9',
  // Shadows
  cardShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)',
  cardHoverShadow: '0 4px 6px rgba(0,0,0,0.07)',
  barShadow: '0 1px 3px rgba(0,0,0,0.08)',
  // Tooltip backgrounds
  tooltipBg: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
  tooltipMatchBg: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
  tooltipPlayerBg: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
};

/* ================================================
   Adapte l'URL Proballers d'un match selon la langue.
   FR : /fr/basketball/match/ID/...
   ES : /es/baloncesto/partido/ID/...
   EN : /basketball/game/ID/...
   ================================================ */
const adaptMatchUrl = (url, lang) => {
  if (!url || lang === 'fr') return url;
  if (lang === 'es') return url.replace('/fr/basketball/match/', '/es/baloncesto/partido/');
  // en (défaut)
  return url.replace('/fr/basketball/match/', '/basketball/game/');
};

/* ================================================
   1) Les types Top 10 que nous allons proposer,
      avec leurs clés i18n.
   ================================================ */
const TOP10_TABS = [
  { key: 'POINTS', tKey: 'points' },
  { key: 'REBONDS', tKey: 'rebounds' },
  { key: 'PASSES', tKey: 'assists' },
  { key: 'STEALS', tKey: 'steals' },
  { key: 'BLOCKS', tKey: 'blocks' },
  { key: 'THREE_PM', tKey: 'threes' },
  { key: 'EVALUATION', tKey: 'evaluation' },
  { key: 'GAME_SCORE', tKey: 'gameScore' }
];

/* ================================================
   Ligues par catégorie pour les tooltips
   MAINTENANT CHARGÉ DYNAMIQUEMENT DEPUIS L'API
   (conservé pour rétrocompatibilité avec l'ancien système)
   ================================================ */
const LEAGUES_BY_CATEGORY_LEGACY = {
  'NBA': ['NBA'],
  'G-LEAGUE': ['G-League'],
  'NCAA': ['NCAA'],
  'COMPETICIONES EUROPEAS': [
    'Basketball Champions League',
    'Eurocup',
    'Euroleague',
    'FIBA Europe Cup'
  ],
  'ELITE': [
    'France – Élite 1 (Pro A)',
    'Germany – easyCredit BBL',
    'Italy – LBA Serie A',
    'Spain – Liga Endesa',
    'Turkey – BSL',
    'FIBA AfroBasket',
    'FIBA AmeriCup',
    'FIBA Asia Cup',
    'FIBA Basketball World Cup',
    'FIBA EuroBasket'
  ],
  'PROYECCIÓN': [
    'Adriatic – ABA Liga',
    'Australia – NBL',
    'Greece – HEBA A1',
    'Israel – Winner League',
    'Lithuania – Betsafe LKL',
    'VTB United League'
  ],
  'OPORTUNIDADES': [
    'BNXT League',
    'Denmark – Ligaen',
    'Finland – Korisliiga',
    'Hungary – A League',
    'Latvian-Estonian Basketball League',
    'Poland – PLK',
    'Portugal – Liga Pro',
    'Romania – Division A',
    'Serbia – KLS',
    'Slovenia – Liga Nova KBM',
    'Super League Basketball',
    'Sweden – Basketligan',
    'Switzerland – SBL'
  ],
  'RESTO DEL MUNDO': [
    'Argentina – Liga A',
    'Basketball Africa League (BAL)',
    'Basketball Champions League Americas',
    'Brazil – NBB',
    'Canada – CEBL',
    'China – CBA',
    'Dominican Republic – LNB',
    'Japan – B1 League',
    'Japan – B2 League',
    'Puerto Rico – BSN'
  ],
  'DIVISIONES INFERIORES': [
    'Adriatic – ABA Liga 2',
    'France – Élite 2 (Pro B)',
    'France – NM1',
    'Germany – Pro A',
    'Germany – Pro B',
    'Greece – HEBA A2',
    'Italy – Serie A2',
    'Lithuania – NKL',
    'Spain – Primera FEB',
    'Spain – Segunda FEB',
    'Turkey – TBL'
  ],
  'CANTERA': [
    'ACB U22',
    'Adidas Next Generation Tournament (ANGT)',
    'Adriatic Junior ABA League U19',
    'France – Élite U21 (Espoirs)',
    'France – U21 Élite 2',
    'Nike Hoop Summit',
    'Russia – U23 Youth Championship',
    'France Young Star Game',
    'FIBA Euro U16',
    'FIBA U16 European Championship Division B',
    'FIBA U16 AmeriCup',
    'FIBA U17 World Cup',
    'FIBA Euro U18',
    'FIBA U18 European Championship Division B',
    'FIBA U18 AmeriCup',
    'FIBA U19 World Cup',
    'FIBA Euro U20',
    'FIBA U20 European Championship Division B'
  ]
};

/* ================================================
   2) groupByTypeTop10 :
      Regroupe les données par type_top10 et garde
      le plus grand id pour chaque type.
   ================================================ */
const groupByTypeTop10 = (data) => {
  const grouped = {};
  data.forEach((item) => {
    const type = item.type_top10;
    if (!grouped[type] || item.id > grouped[type].id) {
      grouped[type] = item;
    }
  });
  return grouped;
};

/* ================================================
   3) parsePlayers :
      Extrait les joueurs (joueur1...joueur10) 
      et retourne un tableau 
      [ 
         {
           pos, 
           name, 
           value, 
           url_joueur, 
           photo_joueur,
           position, 
           ageDecimal,
           equipe,
           ligue,
           result,
           adversaire
         }, 
         ...
      ].
   ================================================ */
const parsePlayers = (row, matchesData = []) => {
  const players = [];
  for (let i = 1; i <= 10; i++) {
    const key = `joueur${i}`;
    if (row[key]) {
      let parsed = null;
      if (typeof row[key] === 'object') {
        parsed = row[key];
      } else if (typeof row[key] === 'string') {
        try {
          parsed = JSON.parse(row[key]);
        } catch (err) {
          console.error(`Erreur de parsing JSON pour ${key}`, err);
        }
      }
      if (parsed) {
        const player = {
          pos: i,
          name: parsed.name,
          value: parsed.value,
          url_joueur: parsed.url_joueur,
          photo_joueur: parsed.photo_joueur,
          // Infos supplémentaires
          position: parsed.infos?.position || '',
          ageDecimal: parsed.infos?.age_decimal || null,
          equipe: parsed.infos?.equipe || '',
          ligue: parsed.infos?.ligue || '',
          result: parsed.infos?.result || '',
          adversaire: parsed.infos?.adversaire || '',
          score: parsed.infos?.score || '',
          // Stats détaillées
          statsDetail: parsed.stats_detail || null,
          // URL du match
          url_match: null
        };

        // Chercher l'URL du match correspondant
        if (player.equipe && matchesData.length > 0) {
          const cleanAdversaire = player.adversaire
            .replace(/^vs\s+/i, '')
            .replace(/^@\s+/i, '')
            .trim();

          // Chercher le match correspondant
          const matchingMatch = matchesData.find(match => {
            const isHome = match.equipe_domicile === player.equipe && match.equipe_exterieur === cleanAdversaire;
            const isAway = match.equipe_exterieur === player.equipe && match.equipe_domicile === cleanAdversaire;
            return isHome || isAway;
          });

          if (matchingMatch) {
            player.url_match = matchingMatch.url_match;
          }
        }

        players.push(player);
      }
    }
  }
  return players;
};

/* ================================================
   4) getPlayerAvatarUrl :
      - Si url_joueur = "players/XXXX", on construit
        l'URL vers notre backend media
      - Sinon on renvoie photo_joueur si présent,
        sinon un avatar par défaut
   ================================================ */
const getPlayerAvatarUrl = (playerUrl, photoJoueur) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  // Cas 1) url_joueur local => "players/XXXX"
  if (playerUrl?.startsWith('players/')) {
    const parts = playerUrl.split('/'); // ["players","1234"]
    const playerId = parts[1];         // "1234"
    if (playerId) {
      return `${getMediaBaseUrl()}/photos/${playerId}.png?${new Date().getTime()}`;
    }
    return null;
  }
  // Cas 2) photo_joueur externe => passer par le proxy backend
  if (photoJoueur) {
    return `${backendUrl}/api/proxy-image?url=${encodeURIComponent(photoJoueur)}`;
  }
  // Cas 3) fallback
  return null;
};

/* ================================================
   5) Composant "ResumeJourneeBlock"
      - Gère l'affichage d'un bloc de stats (titre + 10 joueurs)
      - Sur desktop : un tableau
      - Sur mobile : des cartes (Card)
      - Gère l'icône de téléchargement pour importer
        un joueur externe.
   ================================================ */
const ResumeJourneeBlock = ({
  title,
  row,
  categoryKey,
  currentTypeKey,
  importingPlayers,
  importedPlayers,
  onImportPlayer,
  onSearchEurobasket,
  searchingEurobasket = {},
  leaguesByCategory = {},
  clubPrimaryColor = '#4f46e5',
  clubSecondaryColor = '#FF7043'
}) => {
  const { t, i18n } = useTranslation();
  // Utiliser le mapping dynamique ou le legacy
  const LEAGUES_BY_CATEGORY = Object.keys(leaguesByCategory).length > 0
    ? leaguesByCategory
    : LEAGUES_BY_CATEGORY_LEGACY;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Couleur du club avec alpha pour les fonds légers
  const clubAlpha10 = `${clubPrimaryColor}1A`;
  const clubAlpha15 = `${clubPrimaryColor}26`;

  // Nombre de matchs joués et données des matchs
  const nbMatchJoue = row?.nb_match_joue || row?.matches_data?.length || 0;
  const matchesData = row?.matches_data || [];
  const liguesList = row?.ligues_list || [];

  // Extraction des joueurs
  const players = row ? parsePlayers(row, matchesData) : [];

  // Date d'hier formatée (format court)
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const day = yesterday.getDate().toString().padStart(2, '0');
  const month = (yesterday.getMonth() + 1).toString().padStart(2, '0');
  const dateFormatted = `${day}/${month}`;

  // Titre complet
  const fullTitle = `${title} ${nbMatchJoue ?
    `(${t('resumeJournee.matchCountWithDate', { count: nbMatchJoue, date: dateFormatted })})` :
    ''}`;

  if (!row) {
    return (
      <Paper sx={{
        p: 0,
        borderRadius: 2,
        bgcolor: DESIGN.bgCard,
        border: `1px solid ${DESIGN.border}`,
        overflow: 'hidden',
        boxShadow: DESIGN.cardShadow,
      }} elevation={0}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          px: 1,
          py: 0.5,
          height: '36px',
          background: `linear-gradient(135deg, ${clubPrimaryColor} 0%, ${clubSecondaryColor} 100%)`,
          borderBottom: `1px solid ${DESIGN.border}`,
        }}>
          <Tooltip
            title={
              LEAGUES_BY_CATEGORY[categoryKey] ? (
                <Box sx={{
                  p: 1.2,
                  background: DESIGN.tooltipBg,
                  borderRadius: '6px'
                }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#fff',
                      fontWeight: 'bold',
                      fontSize: '0.85rem',
                      mb: 0.8,
                      borderBottom: '1px solid rgba(255,255,255,0.3)',
                      pb: 0.5
                    }}
                  >
                    {t('resumeJournee.leaguesIncluded')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                    {LEAGUES_BY_CATEGORY[categoryKey]?.map((league, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{
                          width: 5,
                          height: 5,
                          borderRadius: '50%',
                          bgcolor: '#818cf8',
                          flexShrink: 0
                        }}/>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#ecf0f1',
                            fontSize: '0.7rem'
                          }}
                        >
                          {league}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#95a5a6',
                      fontSize: '0.65rem',
                      mt: 1,
                      pt: 0.8,
                      borderTop: '1px solid rgba(255,255,255,0.2)',
                      display: 'block'
                    }}
                  >
                    {t('resumeJournee.leagueCount', { count: LEAGUES_BY_CATEGORY[categoryKey]?.length || 0 })}
                  </Typography>
                </Box>
              ) : null
            }
            placement="top"
            arrow
            enterDelay={500}
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'transparent',
                  '& .MuiTooltip-arrow': {
                    color: '#1e293b',
                  },
                  maxWidth: 350,
                },
              },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: '0.72rem',
                color: '#fff',
                cursor: 'help',
                borderBottom: '1px dashed rgba(255,255,255,0.5)',
                textShadow: '0 1px 2px rgba(0,0,0,0.15)',
                '&:hover': {
                  color: 'rgba(255,255,255,0.85)'
                }
              }}
            >
              {title}
            </Typography>
          </Tooltip>
        </Box>
        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', fontSize: '0.68rem', color: DESIGN.textMuted, fontStyle: 'italic', py: 1.5 }}>
          {t('resumeJournee.noGamesPlayed')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: 0,
        borderRadius: 2,
        bgcolor: DESIGN.bgCard,
        border: `1px solid ${DESIGN.border}`,
        overflow: 'hidden',
        boxShadow: DESIGN.cardShadow,
        transition: 'box-shadow 0.2s ease',
        '&:hover': { boxShadow: DESIGN.cardHoverShadow }
      }}
      elevation={0}
    >
      {/* Titre avec tooltip sur le nombre de matchs */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 1,
        py: 0.5,
        height: '36px',
        bgcolor: DESIGN.bgCardHeader,
        borderBottom: `1px solid ${DESIGN.border}`,
      }}>
        <Tooltip
          title={
            LEAGUES_BY_CATEGORY[categoryKey] ? (
              <Box sx={{
                p: 1.2,
                background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
                borderRadius: '6px'
              }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#fff',
                    fontWeight: 'bold',
                    fontSize: '0.85rem',
                    mb: 0.8,
                    borderBottom: '1px solid rgba(255,255,255,0.3)',
                    pb: 0.5
                  }}
                >
                  {t('resumeJournee.leaguesIncluded')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                  {LEAGUES_BY_CATEGORY[categoryKey]?.map((league, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{
                        width: 5,
                        height: 5,
                        borderRadius: '50%',
                        bgcolor: '#3498db',
                        flexShrink: 0
                      }}/>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#ecf0f1',
                          fontSize: '0.7rem'
                        }}
                      >
                        {league}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#95a5a6',
                    fontSize: '0.65rem',
                    mt: 1,
                    pt: 0.8,
                    borderTop: '1px solid rgba(255,255,255,0.2)',
                    display: 'block'
                  }}
                >
                  {t('resumeJournee.leagueCount', { count: LEAGUES_BY_CATEGORY[categoryKey]?.length || 0 })}
                </Typography>
              </Box>
            ) : null
          }
          placement="top"
          arrow
          enterDelay={500}
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'transparent',
                '& .MuiTooltip-arrow': {
                  color: '#2c3e50',
                },
                maxWidth: 350,
              },
            },
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              fontSize: '0.7rem',
              cursor: 'help',
              borderBottom: '1px dotted #999',
              '&:hover': {
                color: '#1976d2'
              }
            }}
          >
            {title}
          </Typography>
        </Tooltip>
        {(nbMatchJoue > 0 || matchesData.length > 0) && (
          <Tooltip
            title={
              <Box sx={{
                p: 1.5,
                background: DESIGN.tooltipMatchBg,
                borderRadius: '8px',
                maxHeight: '500px',
                overflowY: 'auto'
              }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#fff',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    mb: 1,
                    borderBottom: '1px solid rgba(255,255,255,0.3)',
                    pb: 0.5
                  }}
                >
                  {t('resumeJournee.matchesOfDate', { date: dateFormatted })}
                  <span style={{ fontSize: '0.8rem', fontWeight: 'normal', marginLeft: '8px' }}>
                    ({t('resumeJournee.matchCount', { count: matchesData.length || nbMatchJoue })})
                  </span>
                </Typography>

                {/* Liste des matchs joués groupés par compétition */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {matchesData.length > 0 ? (
                    (() => {
                      // Grouper les matchs par compétition
                      const matchesByLeague = {};
                      matchesData.forEach(match => {
                        const leagueName = match.league || match.league_name || match.source || t('resumeJournee.others');
                        if (!matchesByLeague[leagueName]) {
                          matchesByLeague[leagueName] = [];
                        }
                        matchesByLeague[leagueName].push(match);
                      });

                      // Afficher les matchs groupés par ligue
                      return Object.entries(matchesByLeague).map(([leagueName, matches], leagueIndex) => (
                        <Box key={leagueIndex} sx={{ mb: 1 }}>
                          {/* Nom de la compétition */}
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#93c5fd',
                              fontSize: '0.65rem',
                              fontWeight: 'bold',
                              display: 'block',
                              mb: 0.5,
                              borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                              pb: 0.3
                            }}
                          >
                            {leagueName}
                          </Typography>

                          {/* Matchs de cette compétition */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 1 }}>
                            {matches.map((match, index) => {
                              const isPlayed = match.is_played || match.score !== null;
                              const homeWon = match.score && Number(match.score.split('-')[0]) > Number(match.score.split('-')[1]);
                              const awayWon = match.score && Number(match.score.split('-')[1]) > Number(match.score.split('-')[0]);

                              const matchContent = (
                                <Box sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  p: 0.3,
                                  borderRadius: 1,
                                  background: 'rgba(255, 255, 255, 0.03)',
                                  '&:hover': {
                                    background: 'rgba(255, 255, 255, 0.08)'
                                  },
                                  cursor: match.url_match ? 'pointer' : 'default'
                                }}>
                                  {/* Heure du match */}
                                  <Typography variant="caption" sx={{
                                    color: '#93c5fd',
                                    fontSize: '0.65rem',
                                    minWidth: '30px'
                                  }}>
                                    {match.heure || ''}
                                  </Typography>

                                  {/* Logo équipe domicile */}
                                  {match.logo_domicile && (
                                    <Avatar
                                      src={match.logo_domicile}
                                      sx={{
                                        width: 18,
                                        height: 18,
                                        bgcolor: 'white'
                                      }}
                                    />
                                  )}

                                  {/* Nom équipe domicile */}
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: isPlayed && homeWon ? DESIGN.win : '#fff',
                                      fontSize: '0.7rem',
                                      fontWeight: isPlayed && homeWon ? 'bold' : 'normal',
                                      minWidth: '85px',
                                      textAlign: 'right'
                                    }}
                                  >
                                    {match.equipe_domicile || ''}
                                  </Typography>

                                  {/* Score ou vs */}
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: isPlayed ? '#fbbf24' : '#fff',
                                      fontSize: '0.75rem',
                                      fontWeight: 'bold',
                                      minWidth: '45px',
                                      textAlign: 'center'
                                    }}
                                  >
                                    {match.score || 'vs'}
                                  </Typography>

                                  {/* Nom équipe extérieur */}
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: isPlayed && awayWon ? DESIGN.win : '#fff',
                                      fontSize: '0.7rem',
                                      fontWeight: isPlayed && awayWon ? 'bold' : 'normal',
                                      minWidth: '85px'
                                    }}
                                  >
                                    {match.equipe_exterieur || ''}
                                  </Typography>

                                  {/* Logo équipe extérieur */}
                                  {match.logo_exterieur && (
                                    <Avatar
                                      src={match.logo_exterieur}
                                      sx={{
                                        width: 18,
                                        height: 18,
                                        bgcolor: 'white'
                                      }}
                                    />
                                  )}
                                </Box>
                              );

                              // Si le match a une URL, l'entourer d'un lien
                              if (match.url_match) {
                                return (
                                  <Link
                                    key={index}
                                    href={adaptMatchUrl(match.url_match, i18n.language)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="none"
                                    sx={{
                                      display: 'block',
                                      '&:hover': {
                                        opacity: 0.9
                                      }
                                    }}
                                  >
                                    {matchContent}
                                  </Link>
                                );
                              }

                              return <Box key={index}>{matchContent}</Box>;
                            })}
                          </Box>
                        </Box>
                      ));
                    })()
                  ) : (
                    // Fallback sur l'ancien système si pas de matchesData
                    (() => {
                      const uniqueMatches = new Map();
                      players.forEach(player => {
                        if (player.equipe && player.adversaire) {
                          let cleanAdversaire = player.adversaire
                            .replace(/^vs\s+/i, '')
                            .replace(/^@\s+/i, '')
                            .trim();
                          const teams = [player.equipe, cleanAdversaire].sort();
                          const normalizedKey = `${teams[0]}-${teams[1]}-${player.score || ''}`;
                          if (!uniqueMatches.has(normalizedKey)) {
                            uniqueMatches.set(normalizedKey, {
                              equipe: player.equipe,
                              adversaire: player.adversaire,
                              result: player.result,
                              score: player.score
                            });
                          }
                        }
                      });
                      const matchesArray = Array.from(uniqueMatches.values()).slice(0, 5);
                      if (matchesArray.length === 0) {
                        return (
                          <Typography variant="caption" sx={{ color: '#fff', fontSize: '0.75rem' }}>
                            {t('resumeJournee.noMatchInfoAvailable')}
                          </Typography>
                        );
                      }
                      return matchesArray.map((match, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: match.result === 'W' ? DESIGN.win : match.result === 'L' ? DESIGN.loss : '#fff'
                          }}/>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#fff',
                              fontSize: '0.75rem',
                              display: 'flex',
                              gap: 0.5
                            }}
                          >
                            <span style={{ fontWeight: 'bold' }}>{match.equipe}</span>
                            <span>{match.adversaire}</span>
                            {match.score && (
                              <span style={{ color: '#fbbf24', fontWeight: 'bold' }}>
                                ({match.score})
                              </span>
                            )}
                          </Typography>
                        </Box>
                      ));
                    })()
                  )}
                </Box>

                {/* Call to action */}
                <Box sx={{
                  mt: 1.5,
                  pt: 1,
                  borderTop: '1px solid rgba(255,255,255,0.3)',
                  textAlign: 'center'
                }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#93c5fd',
                      fontSize: '0.7rem',
                      fontStyle: 'italic',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5
                    }}
                  >
                    💡 {t('resumeJournee.clickPlayersForDetails')}
                  </Typography>
                </Box>
              </Box>
            }
            placement="bottom"
            arrow
            enterDelay={200}
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'transparent',
                  '& .MuiTooltip-arrow': {
                    color: '#1e293b',
                  },
                  maxWidth: 600,
                },
              },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: '0.7rem',
                color: '#1976d2',
                cursor: 'pointer',
                textDecoration: 'underline',
                '&:hover': {
                  color: '#1565c0'
                }
              }}
            >
              ({t('resumeJournee.matchCountWithDate', { count: matchesData.length || nbMatchJoue, date: dateFormatted })})
            </Typography>
          </Tooltip>
        )}
      </Box>

      {/* Affichage mobile = Cards */}
      {isMobile ? (
        <Box>
          {players.length > 0 ? (
            players.map((player) => {
              const isLocalLink = player.url_joueur?.startsWith('players/');
              const linkHref = isLocalLink
                ? `/${player.url_joueur}`
                : player.url_joueur;
              const avatarUrl = getPlayerAvatarUrl(
                player.url_joueur,
                player.photo_joueur
              );

              // Couleur du résultat (vert si W, rouge si L, sinon neutre)
              const resultColor =
                player.result === 'W' ? DESIGN.win : player.result === 'L' ? DESIGN.loss : 'inherit';

              return (
                <Card
                  key={player.pos}
                  variant="outlined"
                  sx={{
                    mb: 1,
                    borderRadius: 2,
                    bgcolor: DESIGN.bgCard,
                    borderColor: DESIGN.border,
                  }}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      {/* Position dans le top */}
                      <Box sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        mr: 2,
                        flexShrink: 0,
                        ...(player.pos <= 3 ? {
                          color: '#fff',
                          bgcolor: player.pos === 1 ? DESIGN.gold : player.pos === 2 ? DESIGN.silver : DESIGN.bronze,
                          boxShadow: '0 1px 2px rgba(0,0,0,0.15)'
                        } : {
                          color: DESIGN.textMuted,
                          bgcolor: DESIGN.borderLight,
                        })
                      }}>
                        {player.pos}
                      </Box>

                      {/* Avatar */}
                      <Avatar
                        src={avatarUrl}
                        alt={player.name}
                        sx={{
                          width: 40,
                          height: 40,
                          boxShadow: `0 0 0 1px ${DESIGN.border}`,
                          mr: 2,
                          flexShrink: 0,
                          '& img': {
                            objectFit: 'cover',
                            width: '100%',
                            height: '100%'
                          }
                        }}
                      />

                      {/* Nom du joueur + icône import si externe */}
                      {player.url_joueur ? (
                        isLocalLink ? (
                          // Lien interne
                          <Link
                            component={RouterLink}
                            to={linkHref}
                            underline="none"
                            sx={{ color: DESIGN.textPrimary, fontWeight: 500, '&:hover': { color: clubPrimaryColor } }}
                          >
                            {player.name}
                          </Link>
                        ) : (
                          // Lien externe + bouton import
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            <Link
                              component="button"
                              onClick={(e) => { e.preventDefault(); onSearchEurobasket(player); }}
                              underline="none"
                              sx={{
                                color: DESIGN.textPrimary,
                                fontWeight: 500,
                                cursor: searchingEurobasket[player.name] ? 'wait' : 'pointer',
                                opacity: searchingEurobasket[player.name] ? 0.6 : 1,
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                font: 'inherit',
                                textAlign: 'left',
                                '&:hover': { color: clubPrimaryColor }
                              }}
                            >
                              {searchingEurobasket[player.name] ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {player.name}
                                  <CircularProgress size={14} />
                                </Box>
                              ) : player.name}
                            </Link>
                            {!importedPlayers[player.url_joueur] && (
                              <IconButton
                                onClick={() => onImportPlayer(player)}
                                size="small"
                                sx={{ ml: 1 }}
                              >
                                {importingPlayers[player.url_joueur] ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <DownloadIcon />
                                )}
                              </IconButton>
                            )}
                          </Box>
                        )
                      ) : (
                        // Pas d'URL
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 500, color: DESIGN.textPrimary }}
                        >
                          {player.name}
                        </Typography>
                      )}
                    </Box>

                    {/* Infos supplémentaires : poste, âge, équipe (ligue), adversaire + résultat + score */}
                    <Typography variant="body2" sx={{ ml: '58px' }}>
                      {player.position
                        ? `${player.position}, `
                        : ''}
                      {player.ageDecimal
                        ? `${player.ageDecimal} ${t('resumeJournee.years')}, `
                        : ''}
                      {player.equipe
                        ? `${player.equipe} `
                        : ''}
                      {player.ligue
                        ? `(${player.ligue}) `
                        : ''}
                      {player.adversaire
                        ? `| ${player.adversaire} `
                        : ''}
                      {player.result ? (
                        <Box
                          component="span"
                          sx={{
                            color: resultColor,
                            fontWeight: 'bold',
                            ml: 0.5
                          }}
                        >
                          {player.result}
                        </Box>
                      ) : null}
                      {player.score ? (
                        <Box
                          component="span"
                          sx={{
                            fontWeight: 'bold',
                            ml: 1
                          }}
                        >
                          ({player.score})
                        </Box>
                      ) : null}
                    </Typography>

                    {/* Valeur (stats) */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 1 }}>
                      {t('resumeJournee.total')}: {player.value}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', py: 1.5, color: DESIGN.textMuted, fontStyle: 'italic' }}>{t('resumeJournee.noGamesPlayed')}</Typography>
          )}
        </Box>
      ) : (
        /* Affichage desktop = Table */
        <Table
          sx={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
            '& thead th': {
              backgroundColor: 'transparent',
              borderBottom: `2px solid ${DESIGN.border}`,
              fontWeight: 600,
              padding: '4px 6px',
              fontSize: '0.6rem',
              color: DESIGN.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            },
            '& tbody td': {
              border: 'none',
              borderBottom: `1px solid ${DESIGN.borderLight}`,
              padding: '2px 6px',
              verticalAlign: 'middle'
            },
            '& tbody tr:last-child td': {
              borderBottom: 'none'
            },
            '& tbody tr:hover': {
              backgroundColor: clubAlpha10
            }
          }}
          size="small"
        >
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ width: '20px' }}>#</TableCell>
              <TableCell align="left">{t('resumeJournee.player')}</TableCell>
              <TableCell align="center" sx={{ width: '35px' }}>{t('resumeJournee.total')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {players.length > 0 ? (
              players.map((player) => {
                const isLocalLink = player.url_joueur?.startsWith('players/');
                const linkHref = isLocalLink
                  ? `/${player.url_joueur}`
                  : player.url_joueur;
                const avatarUrl = getPlayerAvatarUrl(
                  player.url_joueur,
                  player.photo_joueur
                );
                const resultColor =
                  player.result === 'W'
                    ? DESIGN.win
                    : player.result === 'L'
                    ? DESIGN.loss
                    : 'inherit';

                return (
                  <TableRow key={player.pos} sx={{
                    height: '26px',
                    lineHeight: '26px',
                    '& > td': {
                      height: '26px',
                      maxHeight: '26px',
                      lineHeight: '26px',
                      overflow: 'hidden',
                      padding: '2px 6px',
                      verticalAlign: 'middle'
                    }
                  }}>
                    {/* Position dans le top */}
                    <TableCell
                      align="center"
                      sx={{ width: '28px' }}
                    >
                      <Box sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.55rem',
                        fontWeight: 700,
                        mx: 'auto',
                        ...(player.pos <= 3 ? {
                          color: '#fff',
                          bgcolor: player.pos === 1 ? DESIGN.gold : player.pos === 2 ? DESIGN.silver : DESIGN.bronze,
                          boxShadow: '0 1px 2px rgba(0,0,0,0.15)'
                        } : {
                          color: DESIGN.textMuted,
                          bgcolor: 'transparent',
                        })
                      }}>
                        {player.pos}
                      </Box>
                    </TableCell>

                    {/* Nom + infos + import */}
                    <TableCell>
                      <Tooltip
                        title={
                          <Box sx={{
                            p: 1,
                            background: DESIGN.tooltipPlayerBg,
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                          }}>
                            {/* Header avec photo et nom du joueur */}
                            <Box sx={{
                              borderBottom: '1px solid rgba(255,255,255,0.3)',
                              pb: 1,
                              mb: 1,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5
                            }}>
                              {/* Photo du joueur */}
                              <Avatar
                                src={avatarUrl}
                                alt={player.name}
                                sx={{
                                  width: 48,
                                  height: 48,
                                  border: '2px solid rgba(255,255,255,0.8)',
                                  boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                                  flexShrink: 0,
                                  '& img': {
                                    objectFit: 'cover',
                                    width: '100%',
                                    height: '100%'
                                  }
                                }}
                              />

                              {/* Nom du joueur */}
                              <Typography
                                variant="body1"
                                sx={{
                                  color: '#fff',
                                  fontWeight: 'bold',
                                  fontSize: '0.95rem',
                                  textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                  flex: 1
                                }}
                              >
                                {player.name}
                              </Typography>
                            </Box>

                            {/* Informations du joueur */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {player.position && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: '#818cf8'
                                  }}/>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: '#fff', fontSize: '0.75rem' }}
                                  >
                                    <strong>{t('resumeJournee.position')}</strong> {player.position}
                                  </Typography>
                                </Box>
                              )}

                              {player.ageDecimal && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: '#a5b4fc'
                                  }}/>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: '#fff', fontSize: '0.75rem' }}
                                  >
                                    <strong>{t('resumeJournee.age')}</strong> {player.ageDecimal} {t('resumeJournee.years')}
                                  </Typography>
                                </Box>
                              )}

                              {player.equipe && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: '#c4b5fd'
                                  }}/>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: '#fff', fontSize: '0.75rem' }}
                                  >
                                    <strong>{t('resumeJournee.team')}</strong> {player.equipe}
                                  </Typography>
                                </Box>
                              )}

                              {player.ligue && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: '#67e8f9'
                                  }}/>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: '#fff', fontSize: '0.75rem' }}
                                  >
                                    <strong>{t('resumeJournee.league')}</strong> {player.ligue}
                                  </Typography>
                                </Box>
                              )}

                              {player.adversaire && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: '#fbbf24'
                                  }}/>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: '#fff', fontSize: '0.75rem' }}
                                  >
                                    <strong>{t('resumeJournee.against')}</strong> {player.adversaire}
                                  </Typography>
                                </Box>
                              )}

                              {(player.result || player.score) && (
                                <Box sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  mt: 0.5,
                                  pt: 0.5,
                                  borderTop: '1px solid rgba(255,255,255,0.2)'
                                }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#fff',
                                      fontSize: '0.75rem',
                                      fontWeight: 'bold',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5
                                    }}
                                  >
                                    <strong>{t('resumeJournee.resultLabel')}</strong>
                                    <Box
                                      component="span"
                                      sx={{
                                        px: 0.8,
                                        py: 0.2,
                                        borderRadius: '4px',
                                        bgcolor: player.result === 'W'
                                          ? DESIGN.win
                                          : player.result === 'L'
                                          ? DESIGN.loss
                                          : 'rgba(255,255,255,0.2)',
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      {player.result}
                                    </Box>
                                    {player.score && (
                                      <Box component="span" sx={{ ml: 0.5 }}>
                                        {player.score}
                                      </Box>
                                    )}
                                  </Typography>
                                </Box>
                              )}

                              {/* Lien vers le match */}
                              {player.url_match && (
                                <Box sx={{
                                  mt: 1,
                                  pt: 0.5,
                                  borderTop: '1px solid rgba(255,255,255,0.2)',
                                  textAlign: 'center'
                                }}>
                                  <Button
                                    href={adaptMatchUrl(player.url_match, i18n.language)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="small"
                                    variant="contained"
                                    sx={{
                                      backgroundColor: 'rgba(255, 215, 0, 0.9)',
                                      color: '#000',
                                      fontWeight: 'bold',
                                      fontSize: '0.65rem',
                                      py: 0.3,
                                      px: 1,
                                      minHeight: 'auto',
                                      '&:hover': {
                                        backgroundColor: 'rgba(255, 215, 0, 1)',
                                        transform: 'scale(1.02)'
                                      }
                                    }}
                                  >
                                    📊 {t('resumeJournee.viewMatch')}
                                  </Button>
                                </Box>
                              )}
                            </Box>

                            {/* Valor destacado con tipo de estadística */}
                            {player.value && (
                              <Box sx={{
                                mt: 1,
                                pt: 1,
                                borderTop: '1px solid rgba(255,255,255,0.3)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.5
                              }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#fff',
                                    fontSize: '0.75rem',
                                    opacity: 0.9,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  }}
                                >
                                  {t(`resumeJournee.stats.${TOP10_TABS.find(tab => tab.key === currentTypeKey)?.tKey || ''}`)}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    color: '#fbbf24',
                                    fontWeight: 'bold',
                                    fontSize: '1rem',
                                    textShadow: '0 1px 3px rgba(0,0,0,0.3)'
                                  }}
                                >
                                  {player.value}
                                </Typography>
                              </Box>
                            )}

                            {/* Estadísticas detalladas */}
                            {player.statsDetail && Object.keys(player.statsDetail).length > 0 && (
                              <Box sx={{
                                mt: 1,
                                pt: 1,
                                borderTop: '1px solid rgba(255,255,255,0.3)',
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: 0.8
                              }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#fff',
                                    fontSize: '0.75rem',
                                    gridColumn: 'span 2',
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    mb: 0.5,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    opacity: 0.9
                                  }}
                                >
                                  {t('resumeJournee.otherStats')}
                                </Typography>

                                {player.statsDetail?.minutes !== undefined && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      bgcolor: '#c084fc'
                                    }}/>
                                    <Typography variant="caption" sx={{ color: '#fff', fontSize: '0.7rem' }}>
                                      <strong>{t('resumeJournee.statMinutes')}</strong> {player.statsDetail.minutes}
                                    </Typography>
                                  </Box>
                                )}

                                {player.statsDetail?.points !== undefined && currentTypeKey !== 'POINTS' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      bgcolor: '#f87171'
                                    }}/>
                                    <Typography variant="caption" sx={{ color: '#fff', fontSize: '0.7rem' }}>
                                      <strong>{t('resumeJournee.statPoints')}</strong> {player.statsDetail.points}
                                    </Typography>
                                  </Box>
                                )}

                                {player.statsDetail?.rebonds !== undefined && currentTypeKey !== 'REBONDS' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      bgcolor: '#34d399'
                                    }}/>
                                    <Typography variant="caption" sx={{ color: '#fff', fontSize: '0.7rem' }}>
                                      <strong>{t('resumeJournee.statRebounds')}</strong> {player.statsDetail.rebonds}
                                    </Typography>
                                  </Box>
                                )}

                                {player.statsDetail?.passes !== undefined && currentTypeKey !== 'PASSES' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      bgcolor: '#38bdf8'
                                    }}/>
                                    <Typography variant="caption" sx={{ color: '#fff', fontSize: '0.7rem' }}>
                                      <strong>{t('resumeJournee.statAssists')}</strong> {player.statsDetail.passes}
                                    </Typography>
                                  </Box>
                                )}

                                {player.statsDetail?.steals !== undefined && currentTypeKey !== 'STEALS' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      bgcolor: '#6ee7b7'
                                    }}/>
                                    <Typography variant="caption" sx={{ color: '#fff', fontSize: '0.7rem' }}>
                                      <strong>{t('resumeJournee.statSteals')}</strong> {player.statsDetail.steals}
                                    </Typography>
                                  </Box>
                                )}

                                {player.statsDetail?.blocks !== undefined && currentTypeKey !== 'BLOCKS' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      bgcolor: '#d8b4fe'
                                    }}/>
                                    <Typography variant="caption" sx={{ color: '#fff', fontSize: '0.7rem' }}>
                                      <strong>{t('resumeJournee.statBlocks')}</strong> {player.statsDetail.blocks}
                                    </Typography>
                                  </Box>
                                )}

                                {player.statsDetail?.three_pm !== undefined && currentTypeKey !== 'THREE_PM' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      bgcolor: '#fbbf24'
                                    }}/>
                                    <Typography variant="caption" sx={{ color: '#fff', fontSize: '0.7rem' }}>
                                      <strong>{t('resumeJournee.statThrees')}</strong> {player.statsDetail.three_pm}
                                    </Typography>
                                  </Box>
                                )}

                                {player.statsDetail?.game_score !== undefined && currentTypeKey !== 'GAME_SCORE' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      bgcolor: '#5eead4'
                                    }}/>
                                    <Typography variant="caption" sx={{ color: '#fff', fontSize: '0.7rem' }}>
                                      <strong>{t('resumeJournee.statGameScore')}</strong> {player.statsDetail.game_score}
                                    </Typography>
                                  </Box>
                                )}

                                {player.statsDetail?.evaluation !== undefined && currentTypeKey !== 'EVALUATION' && (
                                  <Box sx={{
                                    gridColumn: 'span 2',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 0.5,
                                    mt: 0.5,
                                    pt: 0.5,
                                    borderTop: '1px solid rgba(255,255,255,0.2)'
                                  }}>
                                    <Typography variant="caption" sx={{
                                      color: '#fbbf24',
                                      fontSize: '0.75rem',
                                      fontWeight: 'bold'
                                    }}>
                                      <strong>{t('resumeJournee.statEvaluation')}</strong> {player.statsDetail.evaluation}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                        }
                        placement="left"
                        arrow
                        enterDelay={200}
                        enterNextDelay={200}
                        leaveDelay={200}
                        disableInteractive={false}
                        TransitionProps={{
                          timeout: 350,
                        }}
                        slotProps={{
                          popper: {
                            sx: {
                              pointerEvents: 'auto',
                            },
                          },
                        }}
                        PopperProps={{
                          modifiers: [
                            {
                              name: 'flip',
                              enabled: true,
                              options: {
                                fallbackPlacements: ['left', 'top', 'bottom'],
                              },
                            },
                            {
                              name: 'preventOverflow',
                              enabled: true,
                              options: {
                                boundariesElement: 'viewport',
                              },
                            },
                            {
                              name: 'offset',
                              enabled: true,
                              options: {
                                offset: [0, 8],
                              },
                            },
                          ],
                        }}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: 'transparent',
                              '& .MuiTooltip-arrow': {
                                color: '#4f46e5',
                              },
                              maxWidth: 260,
                              zIndex: 1500,
                            },
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '26px', verticalAlign: 'middle', width: '100%', minWidth: 0 }}>
                          {/* Avatar */}
                          <Avatar
                            src={avatarUrl}
                            alt={player.name}
                            sx={{
                              width: 18,
                              height: 18,
                              boxShadow: `0 0 0 1px ${DESIGN.border}`,
                              mr: 0.5,
                              flexShrink: 0,
                              display: 'inline-block',
                              '& img': {
                                objectFit: 'cover',
                                width: '100%',
                                height: '100%'
                              }
                            }}
                          />

                          {/* Nom + icône import si externe */}
                          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, overflow: 'hidden' }}>
                            {player.url_joueur ? (
                              isLocalLink ? (
                                // Lien interne
                                <Link
                                  component={RouterLink}
                                  to={linkHref}
                                  underline="hover"
                                  sx={{
                                    color: DESIGN.textPrimary,
                                    fontWeight: 500,
                                    fontSize: '0.68rem',
                                    display: 'block',
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    '&:hover': { color: clubPrimaryColor }
                                  }}
                                >
                                  {player.name}
                                </Link>
                              ) : (
                                // Lien externe + bouton import
                                <>
                                  <Link
                                    component="button"
                                    onClick={(e) => { e.preventDefault(); onSearchEurobasket(player); }}
                                    underline="hover"
                                    sx={{
                                      color: DESIGN.textPrimary,
                                      fontWeight: 500,
                                      fontSize: '0.68rem',
                                      fontFamily: 'inherit',
                                      cursor: searchingEurobasket[player.name] ? 'wait' : 'pointer',
                                      opacity: searchingEurobasket[player.name] ? 0.6 : 1,
                                      background: 'none',
                                      border: 'none',
                                      padding: 0,
                                      textAlign: 'left',
                                      display: 'block',
                                      minWidth: 0,
                                      overflow: 'hidden',
                                      whiteSpace: 'nowrap',
                                      textOverflow: 'ellipsis',
                                      '&:hover': { color: clubPrimaryColor }
                                    }}
                                  >
                                    {searchingEurobasket[player.name] ? (
                                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                        {player.name}
                                        <CircularProgress size={12} />
                                      </Box>
                                    ) : player.name}
                                  </Link>
                                  {!importedPlayers[player.url_joueur] && (
                                    <IconButton
                                      onClick={() => onImportPlayer(player)}
                                      size="small"
                                      sx={{ ml: 0.5, flexShrink: 0 }}
                                    >
                                      {importingPlayers[player.url_joueur] ? (
                                        <CircularProgress size={16} />
                                      ) : (
                                        <DownloadIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  )}
                                </>
                              )
                            ) : (
                              // Pas d'URL
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 500,
                                  color: DESIGN.textPrimary,
                                  fontSize: '0.68rem',
                                  display: 'block',
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis'
                                }}
                              >
                                {player.name}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Tooltip>
                    </TableCell>

                    {/* Valeur (stats) */}
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.68rem', width: '35px', color: DESIGN.textPrimary }}>
                      {player.value}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={3} sx={{ textAlign: 'center', py: 1.5, fontSize: '0.68rem', color: DESIGN.textMuted, fontStyle: 'italic' }}>{t('resumeJournee.noGamesPlayed')}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
};

/* ================================================
   6) Composant principal "ResumeJournee"
      - Récupère data pour les catégories configurées par club
      - Gère un sélecteur d'onglet (TOP10_TABS)
      - Affiche les blocs dans l'ordre configuré
      - Quand on clique sur l'icône de téléchargement,
        on lance l'import SSE via Proballers.
   ================================================ */
const ResumeJournee = () => {
  const { t } = useTranslation();
  const { primaryColor: clubPrimaryColor, secondaryColor: clubSecondaryColor } = useClubTheme();
  // Variables d'environnement (needed for SSE)
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const section = process.env.REACT_APP_SECTION || 'hommes';

  // Récupérer le club_id depuis le localStorage (stocké à la connexion)
  const clubId = localStorage.getItem('clubId') || '1';

  // Hook pour détecter la taille d'écran
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));

  // État pour les catégories chargées depuis l'API (multi-tenant)
  const [categories, setCategories] = useState([]);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [useNewApi, setUseNewApi] = useState(false);

  // Mapping dynamique des sources par catégorie (pour les tooltips)
  const [leaguesByCategory, setLeaguesByCategory] = useState({});

  // Catégories par défaut (utilisées si l'API échoue ou pour rétrocompatibilité)
  const CATEGORIES_DEFAULT = [
    { key: 'COMPETICIONES EUROPEAS', label: 'TOP COMPETICIONES EUROPEAS' },
    { key: 'ELITE', label: 'TOP ELITE' },
    { key: 'PROYECCIÓN', label: 'TOP PROYECCIÓN' },
    { key: 'OPORTUNIDADES', label: 'TOP OPORTUNIDADES' },
    { key: 'RESTO DEL MUNDO', label: 'TOP RESTO DEL MUNDO' },
    { key: 'NBA', label: 'TOP NBA' },
    { key: 'G-LEAGUE', label: 'TOP G-LEAGUE' },
    { key: 'NCAA', label: 'TOP NCAA' },
    { key: 'DIVISIONES INFERIORES', label: 'TOP DIVISIONES INFERIORES' },
    { key: 'CANTERA', label: 'TOP CANTERA' }
  ];

  // États pour les data de toutes les catégories
  const [categoriesData, setCategoriesData] = useState({});
  const [loadingCategories, setLoadingCategories] = useState({});
  const [errorCategories, setErrorCategories] = useState({});

  // État pour l'onglet courant (type top10)
  const [currentTab, setCurrentTab] = useState(0);

  // État pour l'import :
  // - importingPlayers : { [url]: bool } => vrai si en cours d'import
  // - importedPlayers : { [url]: bool } => vrai si déjà importé
  const [importingPlayers, setImportingPlayers] = useState({});
  const [importedPlayers, setImportedPlayers] = useState({});
  const [searchingEurobasket, setSearchingEurobasket] = useState({});

  // État pour savoir si le menu est ouvert (par défaut ouvert sur large screen)
  const [menuOpen, setMenuOpen] = useState(isLargeScreen);

  // État pour la date sélectionnée (par défaut: hier)
  const getYesterday = () => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return date;
  };

  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateForDisplay = (date) => {
    const month = t(`resumeJournee.months.${date.getMonth()}`);
    return t('resumeJournee.dateDisplayFormat', { day: date.getDate(), month, year: date.getFullYear() });
  };

  // Fonction pour obtenir le nom du mois traduit
  const getLocalizedMonth = (date) => {
    return t(`resumeJournee.months.${date.getMonth()}`);
  };

  const [selectedDate, setSelectedDate] = useState(getYesterday());
  const [isLoadingDate, setIsLoadingDate] = useState(false);

  // Écouter les changements d'état du menu via le localStorage
  useEffect(() => {
    const handleStorageChange = () => {
      const menuState = localStorage.getItem('menuOpen');
      setMenuOpen(menuState === 'true');
    };

    // Écouter les changements du localStorage
    window.addEventListener('storage', handleStorageChange);

    // Vérifier l'état initial
    const initialMenuState = localStorage.getItem('menuOpen');
    if (initialMenuState !== null) {
      setMenuOpen(initialMenuState === 'true');
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Charger la configuration des catégories pour ce club au montage
  useEffect(() => {
    const loadClubConfig = async () => {
      setLoadingConfig(true);
      try {
        // Essayer de charger les catégories depuis la nouvelle API
        const resp = await api.get('/api/resume-journee/categories', {
          params: { club_id: clubId, section }
        });

        if (resp.data && resp.data.length > 0) {
          // Transformer les données de l'API en format attendu
          const loadedCategories = resp.data.map(cat => ({
            key: cat.category_key,
            label: `TOP ${cat.category_label}`
          }));
          setCategories(loadedCategories);
          setUseNewApi(true);
          console.log('Catégories chargées depuis API v2:', loadedCategories);
        } else {
          // Pas de catégories configurées, utiliser les défauts
          setCategories(CATEGORIES_DEFAULT);
          setUseNewApi(false);
        }
      } catch (error) {
        console.log('API v2 non disponible, utilisation de l\'ancienne API:', error.message);
        // L'API v2 n'est pas disponible, utiliser l'ancienne
        setCategories(CATEGORIES_DEFAULT);
        setUseNewApi(false);
        setLeaguesByCategory(LEAGUES_BY_CATEGORY_LEGACY);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadClubConfig();
  }, [clubId]);

  // Catégories effectives à utiliser
  const CATEGORIES = categories.length > 0 ? categories : CATEGORIES_DEFAULT;

  // Fonction pour charger les données pour une date spécifique
  const fetchDataForDate = async (date) => {
    if (loadingConfig) return; // Attendre que la config soit chargée

    setIsLoadingDate(true);

    // Initialiser les états de chargement
    const initialLoadingState = {};
    CATEGORIES.forEach(cat => {
      initialLoadingState[cat.key] = true;
    });
    setLoadingCategories(initialLoadingState);

    // Formater la date au format YYYY-MM-DD
    const formattedDate = formatDateForInput(date);

    if (useNewApi) {
      // Utiliser la nouvelle API v2 (multi-tenant)
      const fetchCategoryV2 = async (categoryKey) => {
        try {
          const resp = await api.get('/api/resume-journee/v2', {
            params: {
              club_id: clubId,
              category: categoryKey,
              section,
              journee: formattedDate
            }
          });

          // Transformer les données du nouveau format vers l'ancien format
          const v2Data = resp.data;
          const transformedData = [];

          // Mettre à jour les sources pour le tooltip
          if (v2Data.sources && v2Data.sources.length > 0) {
            setLeaguesByCategory(prev => ({
              ...prev,
              [categoryKey]: v2Data.sources
            }));
          }

          // Transformer top10 en format compatible avec l'ancien affichage
          if (v2Data.top10) {
            Object.entries(v2Data.top10).forEach(([typeKey, players]) => {
              const row = {
                id: Date.now(),
                ligue: categoryKey,
                journee: v2Data.journee,
                type_top10: typeKey,
                nb_match_joue: v2Data.nb_matches,
                matches_data: v2Data.matches || [],
                ligues_list: v2Data.sources || []
              };

              // Transformer les joueurs au format joueur1, joueur2, etc.
              players.forEach((player, idx) => {
                row[`joueur${idx + 1}`] = {
                  name: player.name,
                  value: player.value,
                  url_joueur: player.infos?.local_id ? `players/${player.infos.local_id}` : player.infos?.url,
                  photo_joueur: player.infos?.photo,
                  infos: {
                    position: player.infos?.position,
                    age_decimal: player.infos?.age,
                    equipe: player.infos?.equipe,
                    ligue: player.infos?.source,
                    adversaire: player.infos?.adversaire,
                    score: player.infos?.score,
                    result: null // Non disponible dans le nouveau format
                  },
                  stats_detail: player.stats
                };
              });

              transformedData.push(row);
            });
          }

          setCategoriesData(prev => ({ ...prev, [categoryKey]: transformedData }));
          setErrorCategories(prev => ({ ...prev, [categoryKey]: null }));
        } catch (error) {
          console.error(`Erreur chargement catégorie ${categoryKey}:`, error);
          setErrorCategories(prev => ({ ...prev, [categoryKey]: error.message }));
        } finally {
          setLoadingCategories(prev => ({ ...prev, [categoryKey]: false }));
        }
      };

      // Lancer les requêtes pour toutes les catégories
      const promises = CATEGORIES.map(cat => fetchCategoryV2(cat.key));
      await Promise.all(promises);
    } else {
      // Utiliser l'ancienne API (rétrocompatibilité)
      const fetchCategory = async (categoryKey) => {
        try {
          const resp = await api.get('/api/resume-journee', {
            params: {
              ligue: categoryKey,
              section,
              journee: formattedDate
            }
          });
          setCategoriesData(prev => ({ ...prev, [categoryKey]: resp.data }));
          setErrorCategories(prev => ({ ...prev, [categoryKey]: null }));
        } catch (error) {
          setErrorCategories(prev => ({ ...prev, [categoryKey]: error.message }));
        } finally {
          setLoadingCategories(prev => ({ ...prev, [categoryKey]: false }));
        }
      };

      // Lancer les requêtes pour toutes les catégories
      const promises = CATEGORIES.map(cat => fetchCategory(cat.key));
      await Promise.all(promises);
    }

    setIsLoadingDate(false);
  };

  // Au montage et quand la date change, on récupère les données
  useEffect(() => {
    if (!loadingConfig) {
      fetchDataForDate(selectedDate);
    }
  }, [selectedDate, loadingConfig, useNewApi]);

  // Regroupe par type_top10 pour toutes les catégories
  const groupedCategories = {};
  CATEGORIES.forEach(cat => {
    if (categoriesData[cat.key]) {
      groupedCategories[cat.key] = groupByTypeTop10(categoriesData[cat.key]);
    }
  });

  // Clef du type courant (selon l'onglet)
  const currentTypeKey = TOP10_TABS[currentTab].key;

  // Changement d'onglet
  const handleChangeTab = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Import via Eurobasket (avec fallback Proballers)
  const handleImportPlayer = async (player) => {
    if (!player?.url_joueur) return;

    const urlKey = player.url_joueur;
    const playerName = player.name || '';
    const clubId = localStorage.getItem('clubId');
    const lang = localStorage.getItem('language') || 'es';

    // On marque l'import comme "en cours"
    setImportingPlayers((prev) => ({ ...prev, [urlKey]: true }));

    try {
      // 1) Chercher l'URL Eurobasket
      const searchUrl = `${backendUrl}/api/resume-journee/search-eurobasket?player_name=${encodeURIComponent(playerName)}&section=${section}${clubId ? `&club_id=${clubId}` : ''}`;
      const searchResp = await fetch(searchUrl);
      const searchData = await searchResp.json();

      if (searchData.eurobasket_url) {
        // 2) Importer via Eurobasket (savePlayerWithEurobasketUrl)
        const importResp = await fetch(`${backendUrl}/api/importar/proballers/save-with-eurobasket`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            playerData: { nom: playerName.toUpperCase() },
            eurobasketUrl: searchData.eurobasket_url,
            proballersPhotoUrl: player.photo_joueur || null,
            section: section,
            club_id: clubId,
            lang: lang
          })
        });

        const importData = await importResp.json();

        if (importData.success || importResp.status === 409) {
          // Import réussi ou joueur déjà existant
          setImportedPlayers((prev) => ({ ...prev, [urlKey]: true }));
        } else {
          console.error('Erreur import Eurobasket:', importData.message);
        }
        setImportingPlayers((prev) => ({ ...prev, [urlKey]: false }));
        return;
      }
    } catch (err) {
      console.error('Erreur recherche/import Eurobasket, fallback Proballers:', err);
    }

    // 3) Fallback: Import SSE via Proballers si Eurobasket non trouvé
    const detailsEncoded = encodeURIComponent(playerName);
    const sseUrl = `${backendUrl}/api/importar/proballers?details=${detailsEncoded}&section=${section}${clubId ? `&club_id=${clubId}` : ''}&lang=${lang}`;

    const eventSource = new EventSource(sseUrl);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'done') {
          setImportedPlayers((prev) => ({ ...prev, [urlKey]: true }));
          setImportingPlayers((prev) => ({ ...prev, [urlKey]: false }));
          eventSource.close();
        } else if (data.type === 'error') {
          console.error('Erreur SSE:', data.message);
          setImportingPlayers((prev) => ({ ...prev, [urlKey]: false }));
          eventSource.close();
        } else if (data.type === 'log') {
          console.log('Log SSE:', data.message);
        }
      } catch (err) {
        console.error('Erreur de parsing SSE:', err);
        setImportingPlayers((prev) => ({ ...prev, [urlKey]: false }));
        eventSource.close();
      }
    };

    eventSource.onerror = (err) => {
      console.error('EventSource.onerror:', err);
      setImportingPlayers((prev) => ({ ...prev, [urlKey]: false }));
      eventSource.close();
    };
  };

  // Recherche URL Eurobasket au clic sur un joueur externe
  const handleSearchEurobasket = async (player) => {
    const playerName = player.name || '';
    const fallbackUrl = player.url_joueur;
    const key = playerName;

    setSearchingEurobasket((prev) => ({ ...prev, [key]: true }));

    try {
      const clubId = localStorage.getItem('clubId');
      const url = `${backendUrl}/api/resume-journee/search-eurobasket?player_name=${encodeURIComponent(playerName)}&section=${section}${clubId ? `&club_id=${clubId}` : ''}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.eurobasket_url) {
        window.open(data.eurobasket_url, '_blank', 'noopener,noreferrer');
      } else {
        // Fallback vers URL Proballers
        window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Erreur recherche Eurobasket:', err);
      // Fallback vers URL Proballers
      window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setSearchingEurobasket((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Vérifier si au moins une catégorie est en cours de chargement
  const isAnyLoading = Object.values(loadingCategories).some(loading => loading);

  // Vérifier s'il y a des erreurs
  const hasAnyError = Object.values(errorCategories).some(error => error !== null && error !== undefined);

  // Rendu
  return (
    <Box sx={{ p: 1, bgcolor: DESIGN.bgPage, minHeight: '100vh' }}>
      {/* Barre de contrôle avec onglets et sélecteur de date */}
      <Box sx={{
        position: 'fixed',
        top: 64,
        left: menuOpen && isLargeScreen ? 200 : 0,
        right: 0,
        backgroundColor: DESIGN.bgTabBar,
        zIndex: 1099,
        boxShadow: DESIGN.barShadow,
        borderBottom: `1px solid ${DESIGN.border}`,
        px: 2,
        py: 0.25,
        transition: 'left 0.3s ease'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          width: '100%'
        }}>
          <Tabs
            value={currentTab}
            onChange={handleChangeTab}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              flex: 1,
              minHeight: 38,
              '& .MuiTabs-indicator': {
                height: 3,
                backgroundColor: clubPrimaryColor,
                borderRadius: '3px 3px 0 0'
              },
              '& .MuiTabs-scrollButtons': {
                color: DESIGN.textSecondary,
                '&.Mui-disabled': {
                  opacity: 0.3
                }
              },
              '& .MuiTab-root': {
                fontSize: '0.8rem',
                fontWeight: 500,
                color: DESIGN.textSecondary,
                textTransform: 'none',
                minWidth: 'auto',
                minHeight: 38,
                px: 2,
                py: 1,
                mx: 0.25,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: `${clubPrimaryColor}10`,
                  color: clubPrimaryColor,
                },
                '&.Mui-selected': {
                  color: clubPrimaryColor,
                  fontWeight: 700,
                }
              }
            }}
          >
            {TOP10_TABS.map((tab, idx) => (
              <Tab
                key={tab.key}
                label={t(`resumeJournee.stats.${tab.tKey}`)}
                value={idx}
              />
            ))}
          </Tabs>

          {/* Sélecteur de date */}
          <TextField
            type="date"
            value={formatDateForInput(selectedDate)}
            onChange={(e) => {
              if (e.target.value) {
                const newDate = new Date(e.target.value + 'T00:00:00');
                setSelectedDate(newDate);
              }
            }}
            inputProps={{
              max: formatDateForInput(new Date()),
              style: {
                fontFamily: 'inherit',
                fontSize: '0.875rem'
              }
            }}
            disabled={isLoadingDate}
            size="small"
            sx={{
              width: 150,
              '& .MuiInputBase-root': {
                backgroundColor: DESIGN.bgCard,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: DESIGN.border,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: clubPrimaryColor,
              }
            }}
          />

          {isLoadingDate && (
            <CircularProgress size={20} />
          )}
        </Box>
      </Box>

      {/* Loading / Erreur / Affichage final */}
      <Box sx={{ mt: 6 }}>
        {loadingConfig ? (
          <Box sx={{ textAlign: 'center', my: 4 }}>
            <CircularProgress />
            <Typography>{t('resumeJournee.loadingClubConfig')}</Typography>
          </Box>
        ) : isAnyLoading ? (
          <Box sx={{ textAlign: 'center', my: 4 }}>
            <CircularProgress />
            <Typography>{t('resumeJournee.loadingData')}</Typography>
          </Box>
        ) : hasAnyError ? (
          <Box>
            <Typography color="error">{t('resumeJournee.errorLoadingCategories')}</Typography>
            {Object.entries(errorCategories).map(([key, error]) =>
              error && (
                <Typography key={key} color="error" variant="body2">
                  {key}: {error}
                </Typography>
              )
            )}
          </Box>
        ) : (
          <Grid container spacing={1}>
            {/* Afficher toutes les catégories en grille */}
            {CATEGORIES.map((category) => (
              <Grid item xs={12} sm={6} md={4} lg={2.4} xl={2.4} key={category.key}>
                <ResumeJourneeBlock
                  title={category.label.replace('TOP ', '')}
                  row={groupedCategories[category.key]?.[currentTypeKey]}
                  categoryKey={category.key}
                  currentTypeKey={currentTypeKey}
                  importingPlayers={importingPlayers}
                  importedPlayers={importedPlayers}
                  onImportPlayer={handleImportPlayer}
                  onSearchEurobasket={handleSearchEurobasket}
                  searchingEurobasket={searchingEurobasket}
                  leaguesByCategory={leaguesByCategory}
                  clubPrimaryColor={clubPrimaryColor}
                  clubSecondaryColor={clubSecondaryColor}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default ResumeJournee;
